import { FC, useEffect } from 'react';
import { TiClipboard } from 'react-icons/ti';
import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useDualCtaTest from '@core/hooks/useDualCtaTest';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import { selectViewCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';
import { PageActions } from '@core/services/newRelic';
import { Program } from '@core/ts/results';
import cn from '@core/utils/classNames';

import { useGetApplicationCtaData } from '../../hooks/useGetApplicationCtaData';
import styles from './Application.module.css';

type Props = {
  program: Program;
  eventingOverrides?: Record<string, any>;
  isRecommender?: boolean;
  schoolId?: string;
};

const Application: FC<Props> = ({ program, eventingOverrides, isRecommender, schoolId }) => {
  const cta = useGetApplicationCtaData({ program });
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const flags = useFeatureFlags();
  const { CtaLabelEA, CtaTitleEA, CtaSubtitleEA, ctaLabelCopyEA, dualCtaEnabled, isTestEnabled } =
    useStartYourApplicationEditableAsset(schoolId?.toString() || '');

  const { DualCtaTestComponent, shouldApplyWrapperStyles } = useDualCtaTest(schoolId);
  const noPii = useSelector((state) => selectTestResultById(state, 'noPii'));

  const { elementViewed, elementClicked } = useElementEvents({
    correlationId: viewCorrelationIdMap[program.id],
    webElement: {
      elementType: 'linkout-cta',
      text: ctaLabelCopyEA || 'Start My Application',
      location: eventingOverrides?.location || 'full-screen',
      htmlId: 'voyager-application-cta',
      name: 'application-cta',
    },
  });

  const onCtaClick = () => {
    elementClicked();
    window.open(cta.url, '_blank');
    const actionParams = {
      ...flags,
      anonymousId: window._Cohesion.anonymousId,
      correlationId: viewCorrelationIdMap[program.id],
    };
    PageActions.ApplicationCtaClicked(actionParams);
  };

  useEffect(() => {
    const actionParams = {
      ...flags,
      anonymousId: window._Cohesion.anonymousId,
      correlationId: viewCorrelationIdMap[program.id],
    };
    if (cta?.label && cta?.url) {
      elementViewed();
      PageActions.ApplicationCtaViewed(actionParams);
    }
  }, [cta]);

  if (!cta || cta.url === '') {
    return null;
  }

  if (noPii) {
    return (
      <div>
        <div className={cn(styles.applicationCta, isRecommender && styles.isRecommender)}>
          <span className={styles.applicationCtaHeading}>
            <TiClipboard size="1.5rem" />
            <h2 className={styles.ctaHeading}>
              <CtaTitleEA>Apply Online Today!</CtaTitleEA>
            </h2>
          </span>
          <p>
            <CtaSubtitleEA>
              Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
            </CtaSubtitleEA>
          </p>
        </div>
        <div className={shouldApplyWrapperStyles ? styles.dualCta : styles.singleCta}>
          {isTestEnabled && dualCtaEnabled && (
            <DualCtaTestComponent schoolId={schoolId || ''} className={cn(styles.ctaButton)} />
          )}
          <button type="button" className={cn(styles.ctaButton, styles.isRecommender)} onClick={onCtaClick}>
            <CtaLabelEA>{cta.label}</CtaLabelEA>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.applicationCta, isRecommender && styles.isRecommender)}>
      <span className={styles.applicationCtaHeading}>
        <TiClipboard size="1.5rem" />
        <h2 className={styles.ctaHeading}>
          <CtaTitleEA>Apply Online Today!</CtaTitleEA>
        </h2>
      </span>
      <p>
        <CtaSubtitleEA>
          Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
        </CtaSubtitleEA>
      </p>
      <div className={shouldApplyWrapperStyles ? styles.dualCta : styles.singleCta}>
        {isTestEnabled && dualCtaEnabled && (
          <DualCtaTestComponent schoolId={schoolId || ''} className={cn(styles.ctaButton)} />
        )}
        <button type="button" className={cn(styles.ctaButton, styles.isRecommender)} onClick={onCtaClick}>
          <CtaLabelEA>{cta.label}</CtaLabelEA>
        </button>
      </div>
    </div>
  );
};

Application.defaultProps = {
  eventingOverrides: {},
};

export default Application;
